/**
 * Music Library Service
 *
 * Fetches and manages the curated background music library.
 * Uses API proxy to avoid CORS issues with direct GCS access.
 */

import type {
  MusicLibraryManifest,
  LibraryTrack,
  ResolvedLibraryTrack,
  MusicCategory,
} from '@/types/musicLibrary';
import { getApiServerUrl } from '@/services/api';

// API endpoint for the music library (proxied through API server to avoid CORS)
const getMusicLibraryUrl = () => `${getApiServerUrl()}/api/music-library`;

// CDN base URL for track files (public bucket - used for audio playback URLs)
const MUSIC_BASE_URL =
  'https://d2hx6jpyk06ley.cloudfront.net/background-music/';

// Cache for the library manifest
let cachedManifest: MusicLibraryManifest | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Fetch the music library manifest from GCS
 * Caches the result to avoid repeated fetches
 */
export async function getMusicLibrary(): Promise<MusicLibraryManifest> {
  const now = Date.now();

  // Return cached manifest if still valid
  if (cachedManifest && now - cacheTimestamp < CACHE_DURATION_MS) {
    return cachedManifest;
  }

  try {
    const response = await fetch(getMusicLibraryUrl(), {
      cache: 'default',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch music library: ${response.status}`);
    }

    const manifest: MusicLibraryManifest = await response.json();

    // Update cache
    cachedManifest = manifest;
    cacheTimestamp = now;

    console.log(
      '[MusicLibrary] Loaded library with',
      manifest.totalTracks,
      'tracks'
    );

    return manifest;
  } catch (error) {
    console.error('[MusicLibrary] Failed to load library:', error);

    // Return cached manifest if available (stale is better than nothing)
    if (cachedManifest) {
      console.warn('[MusicLibrary] Returning stale cached manifest');
      return cachedManifest;
    }

    throw error;
  }
}

/**
 * Get all tracks from the library
 */
export async function getAllTracks(): Promise<LibraryTrack[]> {
  const manifest = await getMusicLibrary();
  return manifest.tracks;
}

/**
 * Get a track by ID
 */
export async function getTrackById(
  trackId: string
): Promise<LibraryTrack | null> {
  const manifest = await getMusicLibrary();
  return manifest.tracks.find((track) => track.id === trackId) || null;
}

/**
 * Get tracks filtered by category
 */
export async function getTracksByCategory(
  category: MusicCategory
): Promise<LibraryTrack[]> {
  const manifest = await getMusicLibrary();
  return manifest.tracks.filter((track) => track.category === category);
}

/**
 * Get tracks filtered by mood
 */
export async function getTracksByMood(mood: string): Promise<LibraryTrack[]> {
  const manifest = await getMusicLibrary();
  const lowerMood = mood.toLowerCase();
  return manifest.tracks.filter((track) =>
    track.mood.some((m) => m.toLowerCase().includes(lowerMood))
  );
}

/**
 * Get tracks filtered by BPM
 */
export async function getTracksByBpm(
  bpm: 'slow' | 'medium' | 'medium-fast' | 'fast'
): Promise<LibraryTrack[]> {
  const manifest = await getMusicLibrary();
  return manifest.tracks.filter((track) => track.bpm === bpm);
}

/**
 * Search tracks by text query (searches title, mood, recommendedFor)
 */
export async function searchTracks(query: string): Promise<LibraryTrack[]> {
  const manifest = await getMusicLibrary();
  const lowerQuery = query.toLowerCase();

  return manifest.tracks.filter(
    (track) =>
      track.title.toLowerCase().includes(lowerQuery) ||
      track.mood.some((m) => m.toLowerCase().includes(lowerQuery)) ||
      track.recommendedFor.some((r) => r.toLowerCase().includes(lowerQuery)) ||
      track.category.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Resolve a track to include the full playback URL
 */
export function resolveTrackUrl(track: LibraryTrack): ResolvedLibraryTrack {
  return {
    ...track,
    url: `${MUSIC_BASE_URL}${encodeURIComponent(track.filename)}`,
  };
}

/**
 * Get all resolved tracks (with URLs)
 */
export async function getAllResolvedTracks(): Promise<ResolvedLibraryTrack[]> {
  const tracks = await getAllTracks();
  return tracks.map(resolveTrackUrl);
}

/**
 * Get all available categories with their info
 */
export async function getCategories(): Promise<
  Record<MusicCategory, { description: string; count: number }>
> {
  const manifest = await getMusicLibrary();
  const categories: Record<
    MusicCategory,
    { description: string; count: number }
  > = {} as Record<MusicCategory, { description: string; count: number }>;

  for (const [key, value] of Object.entries(manifest.categories)) {
    categories[key as MusicCategory] = {
      description: value.description,
      count: value.count,
    };
  }

  return categories;
}

/**
 * Get license information for attribution
 */
export async function getLicenseInfo(): Promise<{
  type: string;
  attribution: string;
  attributionUrl: string;
}> {
  const manifest = await getMusicLibrary();
  return {
    type: manifest.license.type,
    attribution: manifest.license.attribution,
    attributionUrl: manifest.license.attributionUrl,
  };
}

/**
 * Clear the cached manifest (for testing or forced refresh)
 */
export function clearMusicLibraryCache(): void {
  cachedManifest = null;
  cacheTimestamp = 0;
}

/**
 * Check if the music library service is available
 * (Always true since it's a static GCS file)
 */
export function isMusicLibraryAvailable(): boolean {
  return true;
}
